import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Briefcase, ArrowLeft, Bell, CheckCircle2, Mail, Zap, Shield, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "Customer Service", "Data Entry", "Tech Support", "Virtual Assistant",
  "Healthcare", "Sales & Marketing", "Education", "Accounting",
  "Web Development", "Social Media", "Transcription", "Insurance",
];

export default function JobAlerts() {
  const [email, setEmail] = useState('');
  const [selectedCats, setSelectedCats] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const toggleCat = (cat) => {
    setSelectedCats(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  };

  const handleSubmit = async () => {
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/job-alerts/subscribe`, {
        email,
        categories: selectedCats.length > 0 ? selectedCats : ["All"],
      });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setSubmitted(true);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="glass sticky top-0 z-50 border-b border-[#E5E0D8]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#047857] flex items-center justify-center"><Briefcase size={14} className="text-white" strokeWidth={1.5} /></div>
            <span className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>WorkFromJoy</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/jobs" className="text-sm text-[#1C2A24]/70 hover:text-[#047857]">Find Jobs</Link>
            <Link to="/auth"><Button className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-9 px-5 text-sm" data-testid="alerts-signup">Sign Up Free</Button></Link>
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 md:px-12 py-10">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-[#047857] mb-6 hover:opacity-80"><ArrowLeft size={14} strokeWidth={1.5} /> Back to Home</Link>

        {!submitted ? (
          <>
            <div className="text-center mb-10">
              <div className="w-16 h-16 rounded-2xl bg-[#047857]/10 flex items-center justify-center mx-auto mb-5">
                <Bell size={28} className="text-[#047857]" strokeWidth={1.5} />
              </div>
              <h1 className="text-3xl md:text-4xl tracking-tight font-medium" style={{fontFamily:'Outfit'}}>Get WFH Jobs in Your Inbox</h1>
              <p className="text-[#1C2A24]/60 mt-3 max-w-lg mx-auto">We'll send you the best new remote job listings every week. No spam, unsubscribe anytime.</p>
            </div>

            {/* Email Input */}
            <div className="bg-white rounded-3xl border border-[#E5E0D8] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] mb-6" data-testid="alerts-form">
              <label className="text-sm font-medium text-[#1C2A24]/80 mb-2 block" style={{fontFamily:'Outfit'}}>Your Email</label>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#1C2A24]/30" strokeWidth={1.5} />
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com"
                    className="w-full pl-10 h-12 rounded-full bg-[#F4F1E8]/50 border border-[#E5E0D8] text-sm focus:outline-none focus:ring-2 focus:ring-[#047857]" data-testid="alerts-email-input" />
                </div>
              </div>

              {/* Category Selection */}
              <div className="mt-6">
                <label className="text-sm font-medium text-[#1C2A24]/80 mb-3 block" style={{fontFamily:'Outfit'}}>Choose categories (optional)</label>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map(cat => (
                    <button key={cat} onClick={() => toggleCat(cat)}
                      className={`px-4 py-2 rounded-full text-xs border transition-all ${selectedCats.includes(cat) ? 'bg-[#047857] text-white border-[#047857]' : 'bg-white text-[#1C2A24]/60 border-[#E5E0D8] hover:border-[#047857]/30'}`}
                      data-testid={`alerts-cat-${cat.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              <Button onClick={handleSubmit} disabled={!email || loading}
                className="w-full rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-12 mt-6 text-sm" data-testid="alerts-submit">
                {loading ? 'Subscribing...' : 'Subscribe to Job Alerts'}
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-4">
              {[
                { icon: Clock, text: "Weekly curated emails" },
                { icon: Shield, text: "No spam, ever" },
                { icon: Zap, text: "Be first to apply" },
              ].map((b, i) => (
                <div key={i} className="text-center">
                  <div className="w-10 h-10 rounded-xl bg-[#F4F1E8] flex items-center justify-center mx-auto mb-2">
                    <b.icon size={16} className="text-[#047857]" strokeWidth={1.5} />
                  </div>
                  <p className="text-xs text-[#1C2A24]/50">{b.text}</p>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-20" data-testid="alerts-success">
            <div className="w-20 h-20 rounded-full bg-[#A7F3D0]/30 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 size={36} className="text-[#047857]" strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl font-medium mb-3" style={{fontFamily:'Outfit'}}>You're Subscribed!</h2>
            <p className="text-[#1C2A24]/60 mb-8 max-w-md mx-auto">We'll send the best new WFH jobs to {email} every week. Check your inbox for a welcome email.</p>
            <div className="flex gap-3 justify-center">
              <Link to="/jobs"><Button className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white">Browse Jobs Now</Button></Link>
              <Link to="/auth"><Button variant="outline" className="rounded-full border-[#E5E0D8]">Create Account</Button></Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
